// src/lib/api.ts
// API の基底 URL を一元化（Vite / CRA 両対応、.env が無くても動く）

/** .env から API_BASE を解決（Vite優先 → CRA → 空） */
const VITE_BASE =
  (typeof import.meta !== "undefined" && (import.meta as any)?.env?.VITE_API_BASE) ||
  (typeof import.meta !== "undefined" && (import.meta as any)?.env?.VITE_API_BASE_URL);

const CRA_BASE =
  (typeof process !== "undefined" && process.env?.REACT_APP_API_BASE_URL) ||
  (typeof process !== "undefined" && process.env?.REACT_APP_API_BASE);

function resolveBaseFromLocation(): string {
  if (typeof window === "undefined") return "http://localhost:4000";
  const { protocol, hostname, port, origin } = window.location;

  // 1) 開発系（:3000 等で開いているとき）は同一ホストの :4000 を直叩き
  if (port === "3000" || hostname === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    // 開発は http 前提に寄せる（https ローカルは稀）
    const proto = protocol === "https:" ? "http:" : protocol;
    return `${proto}//${hostname}:4000`;
  }

  // 2) 本番ドメインは同一オリジン（nginx で /api を裏の 4000 へプロキシ）
  //    例: https://boat-okuman.com → https://boat-okuman.com
  return origin.replace(/\/+$/, "");
}

/** 最終 BASE（.env 最優先 → 自動解決） */
const BASE = String(VITE_BASE || CRA_BASE || resolveBaseFromLocation()).replace(/\/+$/, "");

/** BASE とパスを連結（// を作らない） */
const url = (path: string) => `${BASE}${path.startsWith("/") ? path : `/${path}`}` as const;

/** レスポンス共通ハンドラ（成功のみ JSON で返す） */
async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(`${res.status} ${msg || res.statusText}`);
  }
  const text = await res.text();
  return (text ? JSON.parse(text) : (null as unknown)) as T;
}

/** 共通の init（Cookie 認証を安定させるため credentials: "include" を既定に） */
function withDefaults(init?: RequestInit): RequestInit {
  return {
    credentials: "include",
    ...init,
    headers: {
      ...(init?.headers || {}),
    },
  };
}

/** GET JSON */
export const getJSON = <T,>(path: string, init?: RequestInit) =>
  fetch(url(path), withDefaults({ method: "GET", ...init })).then(handle<T>);

/** POST JSON */
export const postJSON = <T,>(path: string, body: unknown, init?: RequestInit) =>
  fetch(url(path), withDefaults({
    method: "POST",
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    body: JSON.stringify(body ?? {}),
    ...init,
  })).then(handle<T>);

/** PUT JSON */
export const putJSON = <T,>(path: string, body: unknown, init?: RequestInit) =>
  fetch(url(path), withDefaults({
    method: "PUT",
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    body: JSON.stringify(body ?? {}),
    ...init,
  })).then(handle<T>);

/** PATCH JSON */
export const patchJSON = <T,>(path: string, body: unknown, init?: RequestInit) =>
  fetch(url(path), withDefaults({
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    body: JSON.stringify(body ?? {}),
    ...init,
  })).then(handle<T>);

/** DELETE */
export const deleteJSON = <T,>(path: string, init?: RequestInit) =>
  fetch(url(path), withDefaults({ method: "DELETE", ...init })).then(handle<T>);

/** クエリ文字列ビルダー */
export function qs(params: Record<string, unknown> = {}): string {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    sp.append(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : "";
}

/** multipart/form-data で送る（画像アップロードなど） */
export async function postForm<T>(
  path: string,
  form: FormData,
  init?: RequestInit
): Promise<T> {
  return fetch(url(path), withDefaults({
    method: "POST",
    body: form,
    // Content-Type は boundary 自動付与のため指定しない
    ...init,
  })).then(handle<T>);
}

/** SWR 用の fetcher */
export const swrFetcher = (path: string) => getJSON<any>(path);

/** 外部から BASE を参照したい場合用 */
export const API_BASE = BASE;

/** よく使うパスをエクスポート（任意） */
export const API_PUBLIC = `${BASE}/public`;
export const API_ME = `${BASE}/auth/me`;

/** 画像URLを絶対URLに正規化（/uploads/* → http(s)://.../uploads/*） */
export const toImg = (p?: string) => {
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;           // 既に絶対URL
  if (p.startsWith("/uploads/")) return `${BASE}${p}`; // API側の静的配信
  return p;
};
